import { useEffect, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  usersApi,
  USER_ROLE_LABEL,
  type User,
  type UserRole,
  type NewUser,
} from "../../api.js";
import { toast } from "../Toast.js";
import { Button } from "../ui/Button.js";

const inputStyle: CSSProperties = {
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  padding: "6px 10px",
  fontSize: 13,
  fontFamily: "inherit",
  color: "#0f172a",
};

const fieldLabelStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "#64748b",
  marginBottom: 4,
};

const thStyle: CSSProperties = {
  textAlign: "left",
  fontSize: 12,
  fontWeight: 700,
  color: "#64748b",
  padding: "8px 10px",
  borderBottom: "1px solid #e2e8f0",
};

const tdStyle: CSSProperties = {
  fontSize: 13,
  color: "#0f172a",
  padding: "8px 10px",
  borderBottom: "1px solid #f1f5f9",
};

type FormState = {
  name: string;
  email: string;
  role: UserRole;
};

const EMPTY_FORM: FormState = {
  name: "",
  email: "",
  role: "member",
};

function toNewUser(form: FormState): NewUser {
  return {
    name: form.name.trim(),
    email: form.email.trim(),
    role: form.role,
  };
}

export function UserManagementCard() {
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const meQuery = useQuery({ queryKey: ["users", "me"], queryFn: () => usersApi.me() });
  const listQuery = useQuery({ queryKey: ["users"], queryFn: () => usersApi.list() });

  const isOwner = meQuery.data?.role === "owner";

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
  };

  const startEdit = (user: User) => {
    setEditingId(user.id);
    setForm({
      name: user.name,
      email: user.email,
      role: user.role,
    });
    setFormError(null);
  };

  const invalidateUsers = () => qc.invalidateQueries({ queryKey: ["users"] });

  const createMut = useMutation({
    mutationFn: () => usersApi.create(toNewUser(form)),
    onSuccess: () => {
      invalidateUsers();
      toast("ユーザーを追加しました");
      resetForm();
    },
    onError: (e) => toast(e.message, "error"),
  });

  const updateMut = useMutation({
    mutationFn: (id: string) => usersApi.update(id, toNewUser(form)),
    onSuccess: () => {
      invalidateUsers();
      toast("ユーザー情報を保存しました");
      resetForm();
    },
    onError: (e) => toast(e.message, "error"),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => usersApi.remove(id),
    onSuccess: () => {
      invalidateUsers();
      toast("ユーザーを削除しました");
    },
    onError: (e) => toast(e.message, "error"),
  });

  // 編集中に一覧から削除された場合はフォームをリセット
  useEffect(() => {
    if (editingId && listQuery.data && !listQuery.data.data.some((u) => u.id === editingId)) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listQuery.data, editingId]);

  const submit = () => {
    setFormError(null);
    if (!form.name.trim()) { setFormError("氏名は必須です"); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) { setFormError("メールアドレスの形式が不正です"); return; }
    if (editingId) {
      updateMut.mutate(editingId);
    } else {
      createMut.mutate();
    }
  };

  const handleDelete = (user: User) => {
    if (!window.confirm(`「${user.name}」を削除しますか？`)) return;
    deleteMut.mutate(user.id);
  };

  const isLoading = meQuery.isLoading || listQuery.isLoading;
  const isSaving = createMut.isPending || updateMut.isPending;
  const users = listQuery.data?.data ?? [];

  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 24, maxWidth: 720, marginTop: 20 }}>
      <h2 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 4px" }}>ユーザー管理</h2>
      <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 16px" }}>
        本ソフトウェアにログインする事務所メンバーの追加・権限変更・削除を行います。
        帳票に印字する資格者・登録番号は{" "}
        <Link to="/settings/professions" style={{ color: "#0f766e", fontWeight: 700 }}>資格の種類</Link>
        {" "}またはテンプレート編集の発行者情報で登録してください。
      </p>

      {!isOwner && !isLoading && (
        <div style={{
          background: "#fff7ed", border: "1px solid #fed7aa", color: "#c2410c",
          borderRadius: 8, padding: "8px 12px", fontSize: 12.5, marginBottom: 16,
        }}>
          ユーザーの追加・変更はオーナーのみ行えます。
        </div>
      )}

      {isLoading ? (
        <p style={{ color: "#94a3b8" }}>読み込み中…</p>
      ) : (
        <>
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 20 }}>
            <thead>
              <tr>
                <th style={thStyle}>氏名</th>
                <th style={thStyle}>メール</th>
                <th style={thStyle}>権限</th>
                {isOwner && <th style={thStyle}>操作</th>}
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td style={tdStyle}>{user.name}</td>
                  <td style={tdStyle}>{user.email}</td>
                  <td style={tdStyle}>{USER_ROLE_LABEL[user.role]}</td>
                  {isOwner && (
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      <Button variant="ghost" style={{ padding: "4px 8px", marginRight: 4 }} onClick={() => startEdit(user)}>
                        編集
                      </Button>
                      <Button
                        variant="ghost"
                        style={{ padding: "4px 8px", color: "#dc2626" }}
                        onClick={() => handleDelete(user)}
                        disabled={deleteMut.isPending}
                      >
                        削除
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {isOwner && (
            <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 16 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, margin: "0 0 12px", color: "#334155" }}>
                {editingId ? "ユーザー情報を編集" : "ユーザーを追加"}
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label style={fieldLabelStyle}>氏名</label>
                  <input
                    style={inputStyle}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label style={fieldLabelStyle}>メールアドレス</label>
                  <input
                    style={inputStyle}
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label style={fieldLabelStyle}>権限</label>
                  <select
                    style={inputStyle}
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value as UserRole })}
                  >
                    {(Object.entries(USER_ROLE_LABEL) as [UserRole, string][]).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {formError && (
                <div style={{ color: "#dc2626", fontSize: 12.5, marginBottom: 12 }}>{formError}</div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                {editingId && (
                  <Button variant="outline" onClick={resetForm} disabled={isSaving}>
                    キャンセル
                  </Button>
                )}
                <Button variant="primary" onClick={submit} disabled={isSaving}>
                  {isSaving ? "保存中…" : editingId ? "変更を保存" : "ユーザーを追加"}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
